import { useState } from 'react'
import React from 'react'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import IconButton from '@mui/material/IconButton'
import useStyles from '../_styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Button from '@mui/material/Button'
import { EmptyWorkflowIcon, NoRequesterIcon } from '../../../../assets/icons/icons'
import { WorkflowStepProps } from '../_props'
import { useGetIdsHealthcareservicesHcsIdWorkflowsForAct } from '@toktokdoctech/tokapi-client'
import useCurrentHcs from '../../../../hooks/useCurrentHcs'
import { useDebouncedCallback } from '@workspace/frontend-kit'



export default function WorkflowStep({
  onDataChange,
  onSelectAndContinue,
}: WorkflowStepProps & { onSelectAndContinue?: () => void }) {
  const classes = useStyles()
  const currentHcs = useCurrentHcs()

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null)

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchTerm(value)
  }, 300)

  const {
    data: workflowsData,
    isLoading,
  } = useGetIdsHealthcareservicesHcsIdWorkflowsForAct(currentHcs.id, {
    search: debouncedSearchTerm || undefined,
    skip: 0,
    limit: 50,
  })

  const workflows = workflowsData?.items ?? []
  const totalWorkflows = workflowsData?.total ?? 0
  const hasSearchTerm = debouncedSearchTerm.length > 0

  const handleResetSearch = () => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    debouncedSearch('')
  }

  const handleSelectWorkflow = (workflow: typeof workflows[number]) => {
    setSelectedWorkflowId(workflow.id)
    onDataChange?.({
      id: workflow.id,
      name: workflow.name,
      workflowId: workflow.id,
      workflowLabel: workflow.name,
    })
    if (onSelectAndContinue) {
      onSelectAndContinue()
    }
  }


  return (
    <div className={classes.stepContent}>
      <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
        <Typography className={classes.patientLabel}>Sélectionnez la cible</Typography>
      </div>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Rechercher une cible"
        value={searchTerm}
        onChange={(e) => {
          const value = e.target.value
          setSearchTerm(value)
          debouncedSearch(value)
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon className={classes.clearSearchIcon} />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setSearchTerm('')
                  setDebouncedSearchTerm('')
                  debouncedSearch('')
                }}
                edge="end"
                aria-label="Effacer la recherche"
              >
                <ClearIcon className={classes.clearSearchIcon} fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Paper className={classes.patientList}>
      {isLoading && (
        <div className={classes.noResults}>
          <Typography>Chargement...</Typography>
        </div>
      )}

      {!isLoading && hasSearchTerm && workflows.length > 0 && (
        <div className={classes.resultsCount}>
          <Typography variant="body2">
            {workflows.length} résultat{workflows.length > 1 ? 's' : ''} pour "{debouncedSearchTerm}"
          </Typography>
        </div>
      )}

      {!isLoading && workflows.length > 0 && (
        <List>
          {workflows.map((workflow) => (
            <React.Fragment key={workflow.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleSelectWorkflow(workflow)}
                  className={`
                    ${selectedWorkflowId === workflow.id ? classes.selectedPatient : ''}
                  `}
                >
                  <ListItemText
                    primary={workflow.name}
                    primaryTypographyProps={{ className: classes.patientName }}
                    secondaryTypographyProps={{ className: classes.patientInfo }}
                  />
                  {selectedWorkflowId === workflow.id && (
                    <CheckCircleIcon className={classes.checkIcon} />
                  )}
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {!isLoading && workflows.length === 0 && (
        <div className={classes.noWorkflow}>
          <EmptyWorkflowIcon className={classes.searchRequesterIcon} />
          <Typography className={classes.noPatient}>
            Désolé, aucune cible n’a été trouvée. Vérifiez l'orthographe, ou sélectionnez une cible depuis la liste
          </Typography>
          <Button className={classes.resetFilter} onClick={handleResetSearch}>
            Réinitialiser la recherche
          </Button>
        </div>
      )}
    </Paper>
    </div>
  )
}
